// Thin WebSocket client for /ws-mobile. Browser connects through the
// mobile Express server, which proxies to flightops /ws-mobile with the
// mobile API key injected server-side — the key never reaches this file.
//
// Usage:
//   const conn = openRealtime({ locationId, airplaneId });
//   conn.on('flights', ({ event, data }) => { ... });
//   conn.on('clients', ({ event, data }) => { ... });
//   conn.on('open', () => {});
//   conn.on('close', () => {});
//   conn.unsubscribe();

const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_DELAY_MS = 30000;

function wsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws-mobile`;
}

export function openRealtime({ locationId, airplaneId }) {
  const listeners = { flights: [], clients: [], open: [], close: [] };
  let ws = null;
  let closed = false;
  let reconnectDelay = RECONNECT_DELAY_MS;
  let reconnectTimer = null;

  const emit = (ev, payload) => {
    (listeners[ev] || []).forEach((fn) => {
      try { fn(payload); } catch (err) { console.error('realtime listener error:', err); }
    });
  };

  const connect = () => {
    if (closed) return;
    try {
      ws = new WebSocket(wsUrl());
    } catch (err) {
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      reconnectDelay = RECONNECT_DELAY_MS;
      try {
        ws.send(JSON.stringify({ type: 'subscribe', locationId, airplaneId: airplaneId || null }));
      } catch {}
      emit('open');
    };

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (msg && msg.channel === 'flights') {
        emit('flights', { event: msg.event, data: msg.data });
      } else if (msg && msg.channel === 'clients') {
        emit('clients', { event: msg.event, data: msg.data });
      }
    };

    ws.onclose = () => {
      emit('close');
      scheduleReconnect();
    };

    ws.onerror = () => {
      try { ws && ws.close(); } catch {}
    };
  };

  const scheduleReconnect = () => {
    if (closed) return;
    if (reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
      connect();
    }, reconnectDelay);
  };

  connect();

  return {
    on(ev, fn) {
      if (!listeners[ev]) listeners[ev] = [];
      listeners[ev].push(fn);
      return this;
    },
    unsubscribe() {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      try { ws && ws.close(); } catch {}
      ws = null;
    },
  };
}
